import React, {useEffect, useRef, useState} from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	Box, InputAdornment, IconButton, TextField,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useStyles } from "./AddModalStyles";
import InputText from "../../../Inputs/InputText/InputText";
import { ClearIcon } from "@mui/x-date-pickers";
import Tooltip from "@mui/material/Tooltip";

const AddModal = ({
	open,
	handleClose,
	type,
	inputs,
	targetEntity,
	setInputValues,
	onSubmit,
	textColor = '#000',
	backgroundColor = '#fff'
}) => {
	const textFieldRef = useRef(null);
	const [isError, setIsError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [searchValues, setSearchValues] = useState({});
	const [isScan, setIsScan] = useState(false);
	const handleSearchChange = (key, value) => {
		setSearchValues((prev) => ({ ...prev, [key]: value }));
	};

	useEffect(() => {
		if (isScan && textFieldRef.current) {
			textFieldRef.current.focus();
		}
	}, [isScan, isLoading]);

	const handleChange = (key, value) => {
		const updatedValues = { ...targetEntity, [key]: value };
		setInputValues(updatedValues);
	};

	const dialogStyles = useStyles.dialog(backgroundColor);
	const dialogContentBoxStyles = useStyles.dialogContentBox;
	const formControlStyles = useStyles.formControl(textColor);
	const selectStyles = useStyles.select;
	const menuItemStyles = useStyles.menuItem;
	const addButtonStyles = useStyles.addButton(textColor);

	const filterInputs = (el) => {
		if (type === 'equipment') {
			return el.key !== 'tagType' && el.key !== 'equipmentNumber';
		}
		if (type === 'tag') {
			return true;
		}
	};

	const getFilledTextLabelStyle = (value) => {
		if (value) {
			return {
				'.MuiFormLabel-root': {
					transform: "translate(14px, -20px) scale(0.75)!important",
					fontSize: "14px",
					color: `${textColor}!important`
				}
			}
		} else {
			return {}
		}
	};

	const textLabelStyles = {
		'.MuiFormLabel-root.Mui-focused': {
			transform: "translate(14px, -20px) scale(0.75)"
		},
		'.MuiFormLabel-root': {
			fontSize: "14px",
			color: `${textColor}!important`
		}
	}

	useEffect(() => {
		setIsError(false);
		setIsScan(false)
	}, [])

	const handleModalSubmit = async () => {
		try {
			if (isLoading) {
				return;
			}

			if (isScan && !targetEntity.tagId) {
				setIsError(`Tag ID can not be empty during scan mode`)
				alert("Tag ID can not be empty during scan mode");
				return;
			}

			setIsLoading(true)
			await onSubmit();
			handleChange('tagId', '')
			setIsError('')
			setIsLoading(false)

			if (isScan && textFieldRef.current) {
				textFieldRef.current.focus();
			}
		} catch (e) {
			setIsLoading(false)
			console.log(e);
			setIsError(e.data.message || `Unable to create ${type}`)
			alert(e.data.message || `Unable to create ${type}`);
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleModalSubmit();
		}
	};

	return (
		<Dialog
			sx={dialogStyles}
			open={open}
			onClose={() => {
				setIsError(false);
				setIsScan(false)
				handleClose()
			}}
			onKeyDown={isScan ? () => {} : handleKeyPress}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: "16px" }}>
				<DialogTitle sx={{ fontWeight: 'bold' }}>Add {type}</DialogTitle>
				<IconButton onClick={handleClose}>
					<ClearIcon />
				</IconButton>
			</Box>
			<DialogContent>
				<Box sx={dialogContentBoxStyles}>
					{inputs.filter(filterInputs).map((input, index) => {
						const inputSearch = searchValues[input.key] || ''; // Individual search term for each input

						if (input.key === 'tagId' && type === 'tag') {
							return (
								<FormControl
									fullWidth
									key={index}
									sx={{ ...formControlStyles, ...textLabelStyles, ...getFilledTextLabelStyle(targetEntity?.[input.key]) }}
									onKeyDown={(e) => e.stopPropagation()}
								>
									<TextField
										label={input.label}
										variant="outlined"
										size="small"
										value={targetEntity?.[input.key] || ""}
										onChange={(e) => handleChange(input.key, e.target.value)}
										sx={{ ...selectStyles, fontSize: '14px' }}
										InputProps={{
											endAdornment: targetEntity?.[input.key] && (
												<InputAdornment position="end">
													<IconButton onClick={() => handleChange(input.key, '')}>
														<ClearIcon sx={{ fontSize: "14px" }} />
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</FormControl>
							);
						} else {
							if (input.key === 'tagId') {
								return (
									<Box sx={{ display: 'flex', gap:'5px', alignItems: 'center' }}>
										{
											!isScan
												? <FormControl fullWidth key={index} sx={formControlStyles} onKeyDown={(e) => e.stopPropagation()}>
													<InputLabel sx={useStyles.menuItem}>{input.label}</InputLabel>
													<Select
														value={targetEntity?.[input.key] || ""}
														onChange={(e) => handleChange(input.key, e.target.value)}
														label={input.label}
														sx={selectStyles}
														disabled={type === 'equipment' && input.key === 'equipmentNumber'}
														endAdornment={
															targetEntity?.[input.key] && (
																<InputAdornment sx={{ marginRight: "10px" }} position="end">
																	<IconButton onClick={() => handleChange(input.key, '')}>
																		<ClearIcon sx={{ fontSize: "14px" }} />
																	</IconButton>
																</InputAdornment>
															)
														}
													>
														<MenuItem onKeyDown={(e) => e.stopPropagation()}>
															<InputText
																width="265px"
																value={inputSearch}
																onChangeFn={(value) => handleSearchChange(input.key, value)} // Update specific search term
															/>
														</MenuItem>
														{input.options
															.filter((option) => inputSearch.length ? option.name.toLowerCase().includes(inputSearch.toLowerCase()) : true)
															.map((option, i) => (
																<MenuItem autoFocus={false} sx={menuItemStyles} key={i} value={option.name}>
																	{option.name}
																</MenuItem>
															))}
													</Select>
												</FormControl>
												: <FormControl
													fullWidth
													key={index}
													sx={{ ...useStyles.formControl(textColor, false, isError), ...textLabelStyles, ...getFilledTextLabelStyle(targetEntity?.[input.key]) }}
													onKeyDown={(e) => e.stopPropagation()}
												>
													<TextField
														inputRef={textFieldRef}
														label={input.label}
														variant="outlined"
														size="small"
														disabled={isLoading}
														onBlur={() => {
															setTimeout(() => {
																if (isScan) textFieldRef.current.focus();
															}, 0);
														}}
														value={targetEntity?.[input.key] || ""}
														onChange={(e) => {
															handleChange(input.key, e.target.value)
														}}
														onKeyDown={!isScan ? () => {} : handleKeyPress}
														sx={{ ...selectStyles, fontSize: '14px' }}
														InputProps={{
															endAdornment: targetEntity?.[input.key] && (
																<InputAdornment position="end">
																	<IconButton onClick={() => handleChange(input.key, '')}>
																		<ClearIcon sx={{ fontSize: "14px" }} />
																	</IconButton>
																</InputAdornment>
															),
														}}
													/>
												</FormControl>
										}
										<Tooltip title="Scan Mode">
											<Button
												variant={isScan ? "outlined" : "contained"}
												size="small"
												onClick={() => { setIsScan(!isScan) }}
												sx={{
													boxShadow: 'none',
													height: "34px",
													transform: "translateY(5px)",
													backgroundColor: isScan ? textColor : "#fff",
													color: isScan ?  "#fff" : textColor ,
													border: `1px solid ${textColor}!important`,
												}}>
												<QrCode2Icon />
											</Button>
										</Tooltip>
									</Box>
								)
							} else {
								return (
									<FormControl fullWidth key={index} sx={useStyles.formControl(textColor, true)} onKeyDown={(e) => e.stopPropagation()}>
										<InputLabel sx={useStyles.menuItem}>{input.label}</InputLabel>
										<Select
											value={targetEntity?.[input.key] || ""}
											onChange={(e) => handleChange(input.key, e.target.value)}
											label={input.label}
											sx={selectStyles}
											disabled={type === 'equipment' && input.key === 'equipmentNumber'}
											endAdornment={
												targetEntity?.[input.key] && (
													<InputAdornment sx={{ marginRight: "10px" }} position="end">
														<IconButton onClick={() => handleChange(input.key, '')}>
															<ClearIcon sx={{ fontSize: "14px" }} />
														</IconButton>
													</InputAdornment>
												)
											}
										>
											{input.options
												.filter((option) => inputSearch.length ? option.name.toLowerCase().includes(inputSearch.toLowerCase()) : true)
												.map((option, i) => (
													<MenuItem autoFocus={false} sx={menuItemStyles} key={i} value={option.id}>
														{option.name}
													</MenuItem>
												))}
										</Select>
									</FormControl>
								);
							}
						}
					})}

					<Button
						variant="contained"
						sx={addButtonStyles}
						onClick={handleModalSubmit}
					>
						Add {type}
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default AddModal;
